import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
  ResponseMeta,
} from '@/components/DataTable';
import {
  listPersonalInstalledToolsDataTable,
  getPersonalInstalledToolsFilterOptions,
} from '@kirin/api-client/api/shadow-ai';
import type {
  PersonalInstalledToolListItem,
  PersonalInstalledToolsFilterOptionField,
  PersonalInstalledToolsSortField,
} from '@kirin/api-client/contracts/account-shadow-ai-installed-tools';
import { apiClient } from '@/lib/api-client';

/**
 * Installed-tools table meta: the shared `meta` plus the table's own row total,
 * surfaced through `onMeta` so the header stat counts the same query the table
 * paginates (distinct toolId+category groups), not the legacy tools endpoint.
 */
export type PersonalInstalledToolsMeta = ResponseMeta & {
  total: number;
};

export async function fetchPersonalInstalledTools(
  params: DataFetchParams,
  signal?: AbortSignal
): Promise<
  DataFetchResponse<PersonalInstalledToolListItem, PersonalInstalledToolsMeta>
> {
  const sortField = params.sort?.field as
    | PersonalInstalledToolsSortField
    | undefined;
  const response = await listPersonalInstalledToolsDataTable(
    apiClient,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: sortField,
      sortOrder: params.sort?.order,
      search: params.search,
    },
    { signal }
  );

  return {
    data: response.data,
    pagination: response.pagination,
    meta: {
      activeFilters: response.meta?.activeFilters ?? [],
      search: response.meta?.search,
      total: response.pagination.total,
    },
  };
}

export async function fetchPersonalInstalledToolsFilterOptions(
  field: PersonalInstalledToolsFilterOptionField,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getPersonalInstalledToolsFilterOptions(apiClient, {
    field,
    search: params.search,
    limit: params.limit,
    offset: params.offset,
  });

  return {
    options: response.options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}
